import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { i18n, nextStates, PALLET_STATES, type PalletState } from '@wms/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { type SortDir, useTableSort } from '../lib/sortRows.js';
import { ReceivePalletDialog } from '../components/ReceivePalletDialog.js';
import { ConsolidatePalletDialog } from '../components/ConsolidatePalletDialog.js';
import { UnpackPalletDialog } from '../components/UnpackPalletDialog.js';
import { PalletContentsPanel } from '../components/PalletContentsPanel.js';
import {
  BulkPalletTransitionDialog,
  type BulkPalletTargetState,
} from '../components/BulkPalletTransitionDialog.js';

const { t } = i18n;

/** Owner of a pallet — a storage customer or a partner. */
function palletOwner(row: {
  customer: { code: string; name: string } | null;
  ownerPartner: { id: string; code: string; name: string } | null;
}) {
  return row.customer ?? row.ownerPartner ?? { code: '—', name: '—' };
}

type WarehouseStatus = PalletState | 'AWAITING_INSPECTION' | 'DEPLETED';

const ALL_STATUSES = [
  ...PALLET_STATES,
  'AWAITING_INSPECTION',
  'DEPLETED',
] satisfies readonly WarehouseStatus[];
const PALLET_STATE_VALUES: ReadonlySet<string> = new Set(PALLET_STATES);

function isPalletState(status: WarehouseStatus): status is PalletState {
  return PALLET_STATE_VALUES.has(status);
}

interface PalletRow {
  id: string;
  code: string;
  state: PalletState;
  expectedAt: string | null;
  arrivedAt: string | null;
  dispatchedAt: string | null;
  weightKg: string | null;
  count: number;
  comment: string | null;
  sourceOrder: { id: string; code: string; status: string } | null;
  customerId: string | null;
  customer: { code: string; name: string } | null;
  ownerPartner: { id: string; code: string; name: string } | null;
  destinationPartner: { id: string; code: string; name: string } | null;
  contentsVisibleAt: string | null;
  contentsTouched?: boolean;
  depletedAt: string | null;
  awaitsDepletionConfirmation: boolean;
  _count: { contents: number };
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
}

function PalletComment({ row }: { row: PalletRow }) {
  if (!row.sourceOrder) return <>{row.comment ?? '—'}</>;
  const comment = row.comment ?? '';
  const marker = row.sourceOrder.code;
  const markerAt = comment.indexOf(marker);
  const before = markerAt >= 0 ? comment.slice(0, markerAt) : `${t('order.pack.source_order')}: `;
  const after = markerAt >= 0 ? comment.slice(markerAt + marker.length) : '';
  return (
    <>
      {before}
      <Link
        to={`/orders/${row.sourceOrder.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="font-mono text-foreground hover:underline"
      >
        {marker}
      </Link>
      {after}
    </>
  );
}

function boundPartner(row: PalletRow) {
  return row.ownerPartner ?? row.destinationPartner;
}

function contentIndicator(row: PalletRow) {
  if (!['ARRIVED', 'PARTIALLY_DISPATCHED', 'DAMAGED'].includes(row.state)) return null;
  if (row.depletedAt || row.awaitsDepletionConfirmation) return 'exhausted';
  if (row._count.contents === 0) return 'missing';
  if (boundPartner(row) && !row.contentsVisibleAt) return 'unposted';
  return 'registered';
}

function PalletContentBadge({
  row,
  open,
  panelId,
  onToggle,
}: {
  row: PalletRow;
  open: boolean;
  panelId: string;
  onToggle: () => void;
}) {
  const indicator = contentIndicator(row);
  if (!indicator) return <>—</>;
  const color =
    indicator === 'registered'
      ? `${row.contentsTouched ? 'border-amber-500 ring-1 ring-amber-400' : 'border-emerald-300'} bg-emerald-50 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100`
      : indicator === 'missing' || indicator === 'unposted'
        ? 'border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
        : 'border-border bg-muted text-muted-foreground';
  if (indicator === 'registered')
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        title={row.contentsTouched ? t('pallet.contents.opened') : undefined}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${color}`}
      >
        {t(`pallet.contents.indicator.${indicator}`)}
        {row.contentsTouched && <span className="sr-only"> — {t('pallet.contents.opened')}</span>}
        <svg
          aria-hidden="true"
          viewBox="0 0 10 6"
          className={`h-1.5 w-2.5 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>
    );
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${color}`}>
      {t(`pallet.contents.indicator.${indicator}`)}
    </span>
  );
}

function displayedStatus(row: PalletRow): WarehouseStatus {
  if (row.depletedAt) return 'DEPLETED';
  if (row.awaitsDepletionConfirmation) return 'AWAITING_INSPECTION';
  return row.state;
}

function statusLabel(status: WarehouseStatus): string {
  if (status === 'DEPLETED') return t('pallet.depleted.badge');
  if (status === 'AWAITING_INSPECTION') return t('pallet.depleted.awaiting_badge');
  return t(`pallet.state.${status}`);
}

function canBulkUnpack(row: PalletRow): boolean {
  return (
    Boolean(boundPartner(row)) &&
    !row.contentsVisibleAt &&
    !row.depletedAt &&
    row._count.contents === 0 &&
    ['ARRIVED', 'PARTIALLY_DISPATCHED', 'DAMAGED'].includes(row.state)
  );
}

function genericBulkTargets(row: PalletRow): PalletState[] {
  return nextStates(row.state).filter(
    (state) => state !== 'REPACKED' && state !== 'UNPACKED_TO_STOCK',
  );
}

function canBulkTransition(row: PalletRow): boolean {
  return !row.depletedAt && (row.awaitsDepletionConfirmation || genericBulkTargets(row).length > 0);
}

interface CustomerFilterOption {
  id: string;
  code: string;
  name: string;
}

const STATE_BADGE: Record<PalletState, string> = {
  EXPECTED: 'bg-muted text-muted-foreground border-border',
  ARRIVED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  PARTIALLY_DISPATCHED: 'bg-amber-100 text-amber-800 border-amber-200',
  DISPATCHED: 'bg-sky-100 text-sky-800 border-sky-200',
  UNPACKED_TO_STOCK: 'bg-green-100 text-green-800 border-green-200',
  DAMAGED: 'bg-red-100 text-red-800 border-red-200',
  REPACKED: 'bg-violet-100 text-violet-800 border-violet-200',
  LOST: 'bg-red-200 text-red-900 border-red-300',
  CANCELLED: 'bg-muted text-muted-foreground border-border',
};

const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('lt-LT') : '—');

function PalletStatusBadge({ row }: { row: PalletRow }) {
  if (row.depletedAt) {
    return (
      <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
        {t('pallet.depleted.badge')}
      </span>
    );
  }
  if (row.awaitsDepletionConfirmation) {
    return (
      <span className="inline-flex items-center rounded-full border border-amber-400 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
        {t('pallet.depleted.awaiting_badge')}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATE_BADGE[row.state]}`}
    >
      {t(`pallet.state.${row.state}`)}
    </span>
  );
}

function fmtLocation(p: {
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
}): string {
  const parts = [p.locationRow, p.locationShelf, p.locationPosition].filter((x): x is string =>
    Boolean(x && x.length > 0),
  );
  return parts.length === 0 ? '—' : parts.join('-');
}

const WAREHOUSE_SORT_ACCESSORS = {
  code: (row: PalletRow) => row.code,
  owner: (row: PalletRow) => `${palletOwner(row).code} ${palletOwner(row).name}`,
  state: (row: PalletRow) => statusLabel(displayedStatus(row)),
  contents: (row: PalletRow) => {
    const indicator = contentIndicator(row);
    return indicator ? t(`pallet.contents.indicator.${indicator}`) : '';
  },
  location: (row: PalletRow) => fmtLocation(row),
  expected: (row: PalletRow) => row.expectedAt,
  arrived: (row: PalletRow) => row.arrivedAt,
  dispatched: (row: PalletRow) => row.dispatchedAt,
  count: (row: PalletRow) => row.count,
  comment: (row: PalletRow) => row.comment,
} as const;

interface SortCtl {
  sortKey: string;
  sortDir: SortDir;
  toggle: (key: string) => void;
}

function SortableTh({
  label,
  col,
  sort,
  align,
}: {
  label: string;
  col: string;
  sort: SortCtl;
  align?: 'right';
}) {
  const active = sort.sortKey === col;
  return (
    <th className={`px-3 py-2 font-medium ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => sort.toggle(col)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${align === 'right' ? 'flex-row-reverse' : ''}`}
        aria-label={label}
      >
        <span>{label}</span>
        <span className="text-[10px] text-muted-foreground">
          {active ? (sort.sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

function SelectAllCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={inputRef}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      aria-label={t('pallet.bulk_transition.select_all')}
      title={t('pallet.bulk_transition.select_all')}
    />
  );
}

export function WarehouseLoadPage() {
  const [contentsPreview, setContentsPreview] = useState<{
    pallet: PalletRow;
    layout: 'desktop' | 'mobile';
  } | null>(null);
  const [rows, setRows] = useState<PalletRow[] | null>(null);
  const [customers, setCustomers] = useState<CustomerFilterOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [stateFilter, setStateFilter] = useState<WarehouseStatus | ''>('');
  const [rowFilter, setRowFilter] = useState<string>('');
  const [shelfFilter, setShelfFilter] = useState<string>('');
  const [arrivedFrom, setArrivedFrom] = useState<string>('');
  const [arrivedTo, setArrivedTo] = useState<string>('');
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [consolidateOpen, setConsolidateOpen] = useState(false);
  const [bulkUnpackOpen, setBulkUnpackOpen] = useState(false);
  const [bulkTransitionOpen, setBulkTransitionOpen] = useState(false);
  const [bulkTransitionBusy, setBulkTransitionBusy] = useState(false);
  const [selectedPalletIds, setSelectedPalletIds] = useState<Set<string>>(new Set());
  const [reload, setReload] = useState(0);
  const { user } = useAuth();
  // Receiving pallets is operator work (capability pallet.receive). A storage
  // Customer viewing their own pallets must not see the "Receive" button.
  const canReceive = user?.roles.some((r) => r.role === 'ADMIN' || r.role === 'OPERATOR') ?? false;

  useEffect(() => {
    api<CustomerFilterOption[]>('/pallets/filter-options/customers')
      .then(setCustomers)
      .catch((err: unknown) => setError(String(err)));
  }, []);

  useEffect(() => {
    const qs = new URLSearchParams();
    if (customerFilter) qs.set('customerId', customerFilter);
    if (stateFilter && isPalletState(stateFilter)) qs.set('state', stateFilter);
    if (arrivedFrom) qs.set('arrivedFrom', arrivedFrom);
    if (arrivedTo) qs.set('arrivedTo', arrivedTo);
    const path = qs.toString() ? `/pallets?${qs.toString()}` : '/pallets';
    api<PalletRow[]>(path)
      .then(setRows)
      .catch((err: unknown) => setError(String(err)));
  }, [customerFilter, stateFilter, arrivedFrom, arrivedTo, reload]);

  useEffect(() => {
    if (!contentsPreview) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setContentsPreview(null);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [contentsPreview]);

  function toggleContents(row: PalletRow, layout: 'desktop' | 'mobile') {
    setContentsPreview((current) =>
      current?.pallet.id === row.id && current.layout === layout ? null : { pallet: row, layout },
    );
  }

  const filteredRows = useMemo(() => {
    if (!rows) return null;
    const r = rowFilter.trim().toUpperCase();
    const s = shelfFilter.trim().toUpperCase();
    return rows.filter((row) => {
      if (stateFilter && displayedStatus(row) !== stateFilter) return false;
      if (r && (row.locationRow ?? '').toUpperCase() !== r) return false;
      if (s && (row.locationShelf ?? '').toUpperCase() !== s) return false;
      return true;
    });
  }, [rows, stateFilter, rowFilter, shelfFilter]);

  const counts = useMemo(() => {
    if (!filteredRows) return null;
    const totalPhysical = filteredRows.reduce((sum, r) => sum + (r.count ?? 1), 0);
    return { records: filteredRows.length, physical: totalPhysical };
  }, [filteredRows]);

  const palletSort = useTableSort(filteredRows ?? [], WAREHOUSE_SORT_ACCESSORS, {
    key: 'code',
  });

  const selectedPallets = useMemo(
    () => (rows ?? []).filter((row) => selectedPalletIds.has(row.id)),
    [rows, selectedPalletIds],
  );
  const selectedPartner = useMemo(() => {
    const first = selectedPallets[0] ? boundPartner(selectedPallets[0]) : null;
    if (!first || selectedPallets.some((pallet) => boundPartner(pallet)?.id !== first.id))
      return null;
    return first;
  }, [selectedPallets]);
  const bulkUnpackEligible =
    selectedPallets.length >= 1 && Boolean(selectedPartner) && selectedPallets.every(canBulkUnpack);
  const bulkTargetStates = useMemo<BulkPalletTargetState[]>(() => {
    if (selectedPallets.length === 0) return [];
    const shared = genericBulkTargets(selectedPallets[0]!).filter((target) =>
      selectedPallets.every((pallet) => genericBulkTargets(pallet).includes(target)),
    );
    return selectedPallets.every((pallet) => pallet.awaitsDepletionConfirmation)
      ? ['DEPLETED', ...shared.filter((target) => target !== 'DISPATCHED')]
      : shared;
  }, [selectedPallets]);
  const selectAllScope = useMemo(() => {
    if (!canReceive) return [];
    return (filteredRows ?? []).filter(canBulkTransition);
  }, [canReceive, filteredRows]);
  const allSelectableSelected =
    selectAllScope.length > 0 && selectAllScope.every((row) => selectedPalletIds.has(row.id));
  const someSelectableSelected =
    !allSelectableSelected && selectAllScope.some((row) => selectedPalletIds.has(row.id));

  function togglePallet(row: PalletRow) {
    if (!canBulkTransition(row)) return;
    setSelectedPalletIds((current) => {
      const next = new Set(current);
      if (next.has(row.id)) {
        next.delete(row.id);
        return next;
      }
      next.add(row.id);
      return next;
    });
  }

  function toggleAllVisiblePallets() {
    if (selectAllScope.length === 0) return;
    setSelectedPalletIds((current) => {
      const next = new Set(current);
      if (allSelectableSelected) {
        for (const row of selectAllScope) next.delete(row.id);
      } else {
        for (const row of selectAllScope) next.add(row.id);
      }
      return next;
    });
  }

  async function confirmBulkTransition(
    toState: BulkPalletTargetState,
    reason: string,
    date: string,
  ) {
    setBulkTransitionBusy(true);
    try {
      // A single checkbox selection uses the existing individual operation;
      // keep the atomic bulk endpoints' two-or-more contract unchanged.
      const single = selectedPallets.length === 1 ? selectedPallets[0] : undefined;
      const path = single
        ? `/pallets/${single.id}/${toState === 'DEPLETED' ? 'confirm-depleted' : 'transition'}`
        : '/pallets/bulk-transition';
      await api(path, {
        method: 'POST',
        body: {
          ...(!single ? { palletIds: selectedPallets.map((pallet) => pallet.id) } : {}),
          ...(!single || toState !== 'DEPLETED' ? { toState } : {}),
          reason,
          ...(date ? { date } : {}),
        },
      });
      setBulkTransitionOpen(false);
      setSelectedPalletIds(new Set());
      setReload((count) => count + 1);
    } catch {
      window.alert(t('common.error'));
    } finally {
      setBulkTransitionBusy(false);
    }
  }

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!rows || !filteredRows)
    return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  return (
    <section className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold">{t('warehouse.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('warehouse.subtitle')}
            {counts ? (
              <span className="ml-2 tabular-nums">
                · {counts.records} / {counts.physical}
              </span>
            ) : null}
          </p>
        </div>
        {canReceive ? (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setBulkTransitionOpen(true)}
              disabled={bulkTargetStates.length === 0}
              className="shrink-0 rounded border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              {t('pallet.bulk_transition.action').replace(
                '{{count}}',
                String(selectedPallets.length),
              )}
            </button>
            <button
              type="button"
              onClick={() => setBulkUnpackOpen(true)}
              disabled={!bulkUnpackEligible}
              className="shrink-0 rounded border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              {t('pallet.bulk_unpack.action').replace('{{count}}', String(selectedPallets.length))}
            </button>
            <button
              type="button"
              onClick={() => setConsolidateOpen(true)}
              className="shrink-0 rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              {t('pallet.consolidate.action')}
            </button>
            <button
              type="button"
              onClick={() => setReceiveOpen(true)}
              className="shrink-0 rounded bg-primary text-primary-foreground px-3 py-1.5 text-sm"
            >
              {t('pallet.create.title')}
            </button>
          </div>
        ) : null}
      </header>

      {receiveOpen ? (
        <ReceivePalletDialog
          onClose={() => setReceiveOpen(false)}
          onCreated={() => {
            setReceiveOpen(false);
            setReload((n) => n + 1);
          }}
        />
      ) : null}

      {consolidateOpen ? (
        <ConsolidatePalletDialog
          onClose={() => setConsolidateOpen(false)}
          onConsolidated={() => {
            setConsolidateOpen(false);
            setReload((n) => n + 1);
          }}
        />
      ) : null}

      {bulkUnpackOpen && selectedPartner ? (
        <UnpackPalletDialog
          palletId={selectedPallets.length === 1 ? selectedPallets[0]?.id : undefined}
          palletIds={
            selectedPallets.length > 1 ? selectedPallets.map((pallet) => pallet.id) : undefined
          }
          palletCodes={selectedPallets.map((pallet) => pallet.code)}
          lockedPartner={selectedPartner}
          onClose={() => setBulkUnpackOpen(false)}
          onUnpacked={() => {
            setBulkUnpackOpen(false);
            setSelectedPalletIds(new Set());
            setReload((n) => n + 1);
          }}
        />
      ) : null}

      {bulkTransitionOpen && bulkTargetStates.length > 0 ? (
        <BulkPalletTransitionDialog
          palletCodes={selectedPallets.map((pallet) => pallet.code)}
          targetStates={bulkTargetStates}
          busy={bulkTransitionBusy}
          onClose={() => setBulkTransitionOpen(false)}
          onConfirm={confirmBulkTransition}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">{t('warehouse.filter.customer')}</span>
          <select
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1"
          >
            <option value="">{t('warehouse.filter.all_customers')}</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">{t('warehouse.filter.state')}</span>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value as WarehouseStatus | '')}
            className="rounded border border-border bg-background px-2 py-1"
          >
            <option value="">{t('warehouse.filter.all_states')}</option>
            {ALL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {statusLabel(status)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">{t('location.row')}</span>
          <input
            value={rowFilter}
            onChange={(e) => setRowFilter(e.target.value)}
            maxLength={8}
            className="w-16 rounded border border-border bg-background px-2 py-1 text-center font-mono"
          />
        </label>
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">{t('location.shelf')}</span>
          <input
            value={shelfFilter}
            onChange={(e) => setShelfFilter(e.target.value)}
            maxLength={8}
            className="w-16 rounded border border-border bg-background px-2 py-1 text-center font-mono"
          />
        </label>
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">{t('warehouse.filter.arrived_from')}</span>
          <input
            type="date"
            value={arrivedFrom}
            onChange={(e) => setArrivedFrom(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1"
          />
        </label>
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">{t('warehouse.filter.arrived_to')}</span>
          <input
            type="date"
            value={arrivedTo}
            onChange={(e) => setArrivedTo(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1"
          />
        </label>
        {arrivedFrom || arrivedTo ? (
          <button
            type="button"
            onClick={() => {
              setArrivedFrom('');
              setArrivedTo('');
            }}
            className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
          >
            {t('common.clear')}
          </button>
        ) : null}
      </div>

      {filteredRows.length === 0 ? (
        <div className="rounded border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {t('warehouse.empty')}
        </div>
      ) : (
        <>
          <div className="hidden md:block overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  {canReceive ? (
                    <th className="w-10 px-3 py-2 font-medium">
                      <SelectAllCheckbox
                        checked={allSelectableSelected}
                        indeterminate={someSelectableSelected}
                        disabled={selectAllScope.length === 0}
                        onChange={toggleAllVisiblePallets}
                      />
                    </th>
                  ) : null}
                  <SortableTh label={t('pallet.col.code')} col="code" sort={palletSort} />
                  <SortableTh label={t('warehouse.col.customer')} col="owner" sort={palletSort} />
                  <SortableTh label={t('pallet.col.state')} col="state" sort={palletSort} />
                  <SortableTh label={t('pallet.contents.title')} col="contents" sort={palletSort} />
                  <SortableTh label={t('location.label')} col="location" sort={palletSort} />
                  <SortableTh label={t('pallet.col.expected')} col="expected" sort={palletSort} />
                  <SortableTh label={t('pallet.col.arrived')} col="arrived" sort={palletSort} />
                  <SortableTh
                    label={t('pallet.col.dispatched')}
                    col="dispatched"
                    sort={palletSort}
                  />
                  <SortableTh
                    label={t('pallet.col.count')}
                    col="count"
                    sort={palletSort}
                    align="right"
                  />
                  <SortableTh label={t('pallet.col.comment')} col="comment" sort={palletSort} />
                </tr>
              </thead>
              <tbody>
                {palletSort.sorted.map((row) => {
                  const panelId = `pallet-contents-desktop-${row.id}`;
                  const previewOpen =
                    contentsPreview?.pallet.id === row.id && contentsPreview.layout === 'desktop';
                  return (
                    <Fragment key={row.id}>
                      <tr className="border-t border-border">
                        {canReceive ? (
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedPalletIds.has(row.id)}
                              disabled={!canBulkTransition(row)}
                              onChange={() => togglePallet(row)}
                              aria-label={`${t('pallet.bulk_transition.select')} ${row.code}`}
                            />
                          </td>
                        ) : null}
                        <td className="px-3 py-2 font-mono text-xs">
                          <Link
                            to={`/pallets/${row.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            {row.code}
                          </Link>
                        </td>
                        <td className="px-3 py-2">
                          {row.customerId ? (
                            <Link to={`/customers/${row.customerId}`} className="hover:underline">
                              <span className="font-mono text-xs text-muted-foreground">
                                {palletOwner(row).code}
                              </span>{' '}
                              <span>{palletOwner(row).name}</span>
                            </Link>
                          ) : (
                            <span>
                              <span className="font-mono text-xs text-muted-foreground">
                                {palletOwner(row).code}
                              </span>{' '}
                              <span>{palletOwner(row).name}</span>
                              <span className="ml-1 text-xs text-muted-foreground">
                                ({t('nav.partners')})
                              </span>
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <PalletStatusBadge row={row} />
                        </td>
                        <td className="px-3 py-2">
                          <PalletContentBadge
                            row={row}
                            open={previewOpen}
                            panelId={panelId}
                            onToggle={() => toggleContents(row, 'desktop')}
                          />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{fmtLocation(row)}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {fmtDate(row.expectedAt)}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {fmtDate(row.arrivedAt)}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {fmtDate(row.dispatchedAt)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.count}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          <PalletComment row={row} />
                        </td>
                      </tr>
                      {previewOpen ? (
                        <tr key={`${row.id}-contents`} className="border-t border-emerald-200">
                          <td colSpan={canReceive ? 11 : 10} className="bg-muted/30 px-3 py-2">
                            <PalletContentsPanel id={panelId} pallet={row} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {canReceive ? (
            <label className="md:hidden flex items-center gap-2 text-sm font-medium">
              <SelectAllCheckbox
                checked={allSelectableSelected}
                indeterminate={someSelectableSelected}
                disabled={selectAllScope.length === 0}
                onChange={toggleAllVisiblePallets}
              />
              <span>{t('pallet.bulk_transition.select_all')}</span>
            </label>
          ) : null}

          <ul className="md:hidden space-y-2">
            {palletSort.sorted.map((row) => {
              const panelId = `pallet-contents-mobile-${row.id}`;
              const previewOpen =
                contentsPreview?.pallet.id === row.id && contentsPreview.layout === 'mobile';
              return (
                <li key={row.id} className="rounded-md border border-border p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {canReceive ? (
                        <input
                          type="checkbox"
                          checked={selectedPalletIds.has(row.id)}
                          disabled={!canBulkTransition(row)}
                          onChange={() => togglePallet(row)}
                          aria-label={`${t('pallet.bulk_transition.select')} ${row.code}`}
                        />
                      ) : null}
                      <Link
                        to={`/pallets/${row.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-sm hover:underline"
                      >
                        {row.code}
                      </Link>
                    </div>
                    <PalletStatusBadge row={row} />
                  </div>
                  {row.customerId ? (
                    <Link
                      to={`/customers/${row.customerId}`}
                      className="block text-sm hover:underline"
                    >
                      <span className="font-mono text-xs text-muted-foreground mr-2">
                        {palletOwner(row).code}
                      </span>
                      {palletOwner(row).name}
                    </Link>
                  ) : (
                    <div className="text-sm">
                      <span className="font-mono text-xs text-muted-foreground mr-2">
                        {palletOwner(row).code}
                      </span>
                      {palletOwner(row).name}
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({t('nav.partners')})
                      </span>
                    </div>
                  )}
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                    <dt className="text-muted-foreground">{t('pallet.contents.title')}</dt>
                    <dd className="text-right">
                      <PalletContentBadge
                        row={row}
                        open={previewOpen}
                        panelId={panelId}
                        onToggle={() => toggleContents(row, 'mobile')}
                      />
                    </dd>
                    <dt className="text-muted-foreground">{t('location.label')}</dt>
                    <dd className="text-right font-mono">{fmtLocation(row)}</dd>
                    <dt className="text-muted-foreground">{t('pallet.col.expected')}</dt>
                    <dd className="text-right">{fmtDate(row.expectedAt)}</dd>
                    <dt className="text-muted-foreground">{t('pallet.col.arrived')}</dt>
                    <dd className="text-right">{fmtDate(row.arrivedAt)}</dd>
                    <dt className="text-muted-foreground">{t('pallet.col.dispatched')}</dt>
                    <dd className="text-right">{fmtDate(row.dispatchedAt)}</dd>
                    <dt className="text-muted-foreground">{t('pallet.col.count')}</dt>
                    <dd className="text-right tabular-nums">{row.count}</dd>
                  </dl>
                  {previewOpen ? <PalletContentsPanel id={panelId} pallet={row} /> : null}
                  {row.comment ? (
                    <p className="text-xs text-muted-foreground">
                      <PalletComment row={row} />
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      )}

      <div className="rounded-md border border-dashed border-border p-6">
        <h3 className="text-sm font-semibold mb-1">{t('warehouse.floor_plan_title')}</h3>
        <p className="text-sm text-muted-foreground">{t('warehouse.floor_plan_placeholder')}</p>
      </div>
    </section>
  );
}
